import { CodeStub } from '@icure/cardinal-sdk'

export type QualityType = 'nurse' | 'doctor' | 'dietician'

export type QualityCodeType = 'persphysician' | 'persnurse' | 'persdietician'

const QUALITY_TAG_TYPE = 'CD-HCPARTY'
const QUALITY_TAG_VERSION = '1'

// Mapping between quality types and their code equivalents
const QUALITY_TO_CODE_MAP: Record<QualityType, QualityCodeType> = {
  doctor: 'persphysician',
  nurse: 'persnurse',
  dietician: 'persdietician',
}

const CODE_TO_QUALITY_MAP: Record<QualityCodeType, QualityType> = {
  persphysician: 'doctor',
  persnurse: 'nurse',
  persdietician: 'dietician',
}

// All available quality codes
export const QUALITY_CODES: QualityCodeType[] = ['persphysician', 'persnurse', 'persdietician']

/**
 * Returns available quality options for UI with translations
 * @param t - Translation function
 */
export const getAvailableQualities = (t: (key: string) => string): { value: QualityType; label: string }[] => [
  { value: 'doctor', label: t('modalManageAccountForm.hubs.qualities.doctor') },
  { value: 'nurse', label: t('modalManageAccountForm.hubs.qualities.nurse') },
  { value: 'dietician', label: t('modalManageAccountForm.hubs.qualities.dietician') },
]

/**
 * Converts a quality type to its code equivalent
 */
export const qualityToCode = (quality: QualityType): QualityCodeType => {
  return QUALITY_TO_CODE_MAP[quality]
}

/**
 * Converts a quality code to its quality type
 */
export const codeToQuality = (code: QualityCodeType): QualityType => {
  return CODE_TO_QUALITY_MAP[code]
}

/**
 * Creates a CodeStub for a given quality type
 */
export const createQualityTag = (quality: QualityType): CodeStub => {
  const code = qualityToCode(quality)
  return new CodeStub({
    id: `${QUALITY_TAG_TYPE}|${code}|${QUALITY_TAG_VERSION}`,
    type: QUALITY_TAG_TYPE,
    code,
    version: QUALITY_TAG_VERSION,
  })
}

/**
 * Extracts the quality type from a practitioner's type field or codes
 */
export const extractQualityFromPractitioner = (type?: string, codes?: CodeStub[]): QualityType | undefined => {
  // Try to extract from codes first (more reliable)
  if (codes) {
    const qualityCode = codes.find((code) => code.code && QUALITY_CODES.includes(code.code as QualityCodeType))?.code as QualityCodeType | undefined
    if (qualityCode) {
      return codeToQuality(qualityCode)
    }
  }

  // Fallback to type field
  if (type && QUALITY_CODES.includes(type as QualityCodeType)) {
    return codeToQuality(type as QualityCodeType)
  }

  return undefined
}

/**
 * Filters out quality codes from a list of CodeStubs
 */
export const filterOutQualityCodes = (codes?: CodeStub[]): CodeStub[] => {
  return codes?.filter((code) => !code.code || !QUALITY_CODES.includes(code.code as QualityCodeType)) ?? []
}

/**
 * Checks if a code is a quality code
 */
export const isQualityCode = (code: string): boolean => {
  return QUALITY_CODES.includes(code as QualityCodeType)
}
